import { Form } from "@unform/web";
import { Building } from "lucide-react";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import Input from "~/components/RegisterForm/Input";
import RegisterFormMenu from "~/components/RegisterForm/Menu";
import InputLine from "~/components/RegisterForm/InputLine";
import InputLineGroup from "~/components/RegisterForm/InputLineGroup";
import FormHeader from "~/components/RegisterForm/FormHeader";
import Preview from "~/components/Preview";
import { Zoom, toast } from "react-toastify";
import api from "~/services/api";
import { getRegistries, handleUpdatedFields } from "~/functions";
import SelectPopover from "~/components/RegisterForm/SelectPopover";
import FormLoading from "~/components/RegisterForm/FormLoading";
import { useSelector } from "react-redux";
import { FullGridContext } from "../..";
import FindGeneric from "~/components/Finds/FindGeneric";
import { AlertContext } from "~/App";

export const InputContext = createContext({});

const discountTypeOptions = [
  { value: "percent", label: "Percent (%)" },
  { value: "value", label: "Value ($)" },
];

export default function PagePreview({
  access,
  id,
  defaultFormType = "preview",
}) {
  const { alertBox } = useContext(AlertContext);
  const {
    handleOpened,
    setOpened,
    successfullyUpdated,
    setSuccessfullyUpdated,
  } = useContext(FullGridContext);
  const [pageData, setPageData] = useState({
    name: "",
    loaded: false,
    description: "",
    status: "",
    discount_type: "percent",
    discount_value: null,
    paymentmethod_id: null,
    paymentMethod: null,
    company: { name: "" },
    filial_id: null,
    filial: { name: "" },
  });

  const [registry, setRegistry] = useState({
    created_by: null,
    created_at: null,
    updated_by: null,
    updated_at: null,
    canceled_by: null,
    canceled_at: null,
  });
  const [formType, setFormType] = useState(defaultFormType);
  const [fullscreen, setFullscreen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("general");

  const auth = useSelector((state) => state.auth);

  const generalForm = useRef();

  function handleCloseForm() {
    if (!successfullyUpdated) {
      toast("Changes discarted!", { autoClose: 1000 });
    }
    handleOpened(null);
  }

  function handleRemovePaymentMethod() {
    setPageData((prev) => ({
      ...prev,
      paymentmethod_id: null,
      paymentMethod: null,
    }));
    setSuccessfullyUpdated(false);
  }

  function handleDelete() {
    alertBox({
      title: "Attention!",
      descriptionHTML: `<p>Are you sure you want to delete this settlement type?</p>`,
      buttons: [
        {
          title: "No",
          class: "cancel",
        },
        {
          title: "Yes",
          onPress: async () => {
            try {
              await api.delete(`/settlementtypes/${id}`);
              toast("Settlement type deleted!", { autoClose: 1000 });
              setSuccessfullyUpdated(true);
              handleOpened(null);
            } catch (err) {
              if (err?.response?.data?.error) {
                toast(err.response.data.error, {
                  type: "error",
                  autoClose: 3000,
                });
              }
            }
          },
        },
      ],
    });
  }

  async function handleGeneralFormSubmit(data) {
    if (successfullyUpdated) {
      toast("No need to be saved!", {
        autoClose: 1000,
        type: "info",
        transition: Zoom,
      });
      return;
    }
    if (id === "new") {
      try {
        const response = await api.post(`/settlementtypes`, data);
        setOpened(response.data.id);
        setPageData({ ...pageData, ...data });
        setSuccessfullyUpdated(true);
        toast("Saved!", { autoClose: 1000 });
        handleOpened(null);
      } catch (err) {
        if (err?.response?.data?.error) {
          toast(err.response.data.error, { type: "error", autoClose: 3000 });
        }
      }
    } else if (id !== "new") {
      const updated = handleUpdatedFields(data, pageData);

      if (updated.length > 0) {
        const objUpdated = Object.fromEntries(updated);
        try {
          await api.put(`/settlementtypes/${id}`, objUpdated);
          setPageData({ ...pageData, ...objUpdated });
          setSuccessfullyUpdated(true);
          toast("Saved!", { autoClose: 1000 });
          handleOpened(null);
        } catch (err) {
          toast(err.response.data.error, { type: "error", autoClose: 3000 });
        }
      }
    }
  }

  useEffect(() => {
    async function getPageData() {
      try {
        const { data } = await api.get(`/settlementtypes/${id}`);
        setPageData({ ...data, loaded: true });
        const {
          created_by,
          created_at,
          updated_by,
          updated_at,
          canceled_by,
          canceled_at,
        } = data;
        const registries = await getRegistries({
          created_by,
          created_at,
          updated_by,
          updated_at,
          canceled_by,
          canceled_at,
        });
        setRegistry(registries);
      } catch (err) {
        if (err?.response?.data?.error) {
          toast(err.response.data.error, { type: "error", autoClose: 3000 });
        }
      }
    }
    if (id === "new") {
      setFormType("full");
    } else if (id) {
      getPageData();
    }
  }, []);

  return (
    <Preview formType={formType} fullscreen={fullscreen}>
      {pageData ? (
        formType === "preview" ? (
          <div className="border h-full rounded-xl overflow-hidden flex flex-col justify-start gap-1 overflow-y-scroll">
            <div className="relative bg-gray-100 h-16 px-4 py-2 flex flex-row items-start justify-start">
              <button
                onClick={() => setFormType("full")}
                className="absolute top-2 right-20 text-md font-bold bg-mila_orange text-white rounded-md p-1 px-2 h-6 flex flex-row items-center justify-center text-xs gap-1"
              >
                Open
              </button>
              <button
                onClick={() => handleOpened(null)}
                className="absolute top-2 right-2 text-md font-bold bg-secondary rounded-md p-1 px-2 h-6 flex flex-row items-center justify-center text-xs gap-1"
              >
                Close
              </button>
              <h2 style={{ fontSize: 24 }}>{pageData.description}</h2>
            </div>
          </div>
        ) : (
          <div className="flex h-full flex-row items-start justify-between gap-4">
            <div className="flex flex-col items-center justify-between text-xs w-32 gap-4">
              <RegisterFormMenu
                setActiveMenu={setActiveMenu}
                activeMenu={activeMenu}
                name="general"
              >
                <Building size={16} /> General
              </RegisterFormMenu>
            </div>
            <div className="border h-full rounded-xl overflow-hidden flex flex-1 flex-col justify-start">
              <div className="flex flex-col items-start justify-start text-sm overflow-y-scroll">
                <Form
                  ref={generalForm}
                  onSubmit={handleGeneralFormSubmit}
                  className="w-full pb-32"
                >
                  <InputContext.Provider
                    value={{
                      id,
                      generalForm,
                      setSuccessfullyUpdated,
                      fullscreen,
                      setFullscreen,
                      successfullyUpdated,
                      handleCloseForm,
                    }}
                  >
                    {id === "new" || pageData.loaded ? (
                      <>
                        <FormHeader
                          access={access}
                          title={pageData.description?.slice(0, 30)}
                          registry={registry}
                          InputContext={InputContext}
                          otherButtons={
                            id !== "new"
                              ? [
                                  {
                                    icon: "Trash",
                                    text: "Delete",
                                    onClick: handleDelete,
                                  },
                                ]
                              : null
                          }
                        />

                        <InputLineGroup
                          title="GENERAL"
                          activeMenu={activeMenu === "general"}
                        >
                          <FindGeneric
                            route="filials"
                            title="Filial"
                            scope="filial"
                            required
                            InputContext={InputContext}
                            defaultValue={
                              id === "new" && auth.filial.id !== 1
                                ? {
                                    id: auth.filial.id,
                                    name: auth.filial.name,
                                  }
                                : {
                                    id: pageData.filial?.id,
                                    name: pageData.filial?.name,
                                  }
                            }
                            fields={[
                              {
                                title: "Name",
                                name: "name",
                              },
                            ]}
                          />
                          <InputLine title="Description">
                            <Input
                              name="description"
                              required
                              title="Description"
                              grow
                              defaultValue={pageData.description}
                              InputContext={InputContext}
                            />
                          </InputLine>
                          <InputLine title="Status">
                            <Input
                              name="status"
                              required
                              title="Status"
                              grow
                              defaultValue={pageData.status}
                              InputContext={InputContext}
                            />
                          </InputLine>
                          <InputLine title="Discount">
                            <SelectPopover
                              name="discount_type"
                              required
                              title="Discount Type"
                              grow
                              defaultValue={discountTypeOptions.find(
                                (type) => type.value === pageData.discount_type
                              )}
                              options={discountTypeOptions}
                              InputContext={InputContext}
                            />
                            <Input
                              type="number"
                              name="discount_value"
                              required
                              min="0"
                              title="Discount Value"
                              grow
                              defaultValue={pageData.discount_value}
                              InputContext={InputContext}
                            />
                          </InputLine>
                          <FindGeneric
                            route="paymentmethods"
                            title="Payment Method"
                            scope="paymentMethod"
                            InputContext={InputContext}
                            handleRemove={
                              pageData.paymentMethod?.id
                                ? handleRemovePaymentMethod
                                : null
                            }
                            defaultValue={
                              pageData.paymentMethod?.id
                                ? {
                                    id: pageData.paymentMethod.id,
                                    description:
                                      pageData.paymentMethod.description,
                                    platform: pageData.paymentMethod.platform,
                                  }
                                : { id: null, description: null, platform: null }
                            }
                            fields={[
                              {
                                title: "Description",
                                name: "description",
                              },
                              {
                                title: "Platform",
                                name: "platform",
                              },
                            ]}
                          />
                        </InputLineGroup>
                      </>
                    ) : (
                      <FormLoading />
                    )}
                  </InputContext.Provider>
                </Form>
              </div>
            </div>
          </div>
        )
      ) : null}
    </Preview>
  );
}
